// klaviyo-back-in-stock.js

const KLAVIYO_DEFAULT_API_KEY = "XgzCjn";
const KLAVIYO_API_REVISION = "2024-07-15";

/**
 * Sends a back-in-stock subscription request to Klaviyo.
 * If third-party storefront scripts (e.g. Global-e / Web Pixel Manager) broke window.fetch,
 * falls back to a clean native fetch via an iframe context.
 */
async function executeKlaviyoSubscription(apiKey, payload) {
  const url = `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${apiKey}`;
  const options = {
    method: "POST",
    mode: "cors",
    headers: {
      revision: KLAVIYO_API_REVISION,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(payload),
  };

  try {
    return await window.fetch(url, options);
  } catch (windowFetchError) {
    try {
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      document.body.appendChild(iframe);
      const cleanFetch = iframe.contentWindow.fetch;
      const res = await cleanFetch(url, options);
      window.setTimeout(() => iframe.remove(), 1000);
      return res;
    } catch {
      throw windowFetchError;
    }
  }
}

/**
 * Returns the modal controller instance, importing it dynamically if needed.
 */
async function getModalController() {
  if (typeof window === "undefined") return null;
  if (window.__quickfireModalController) {
    return window.__quickfireModalController;
  }
  try {
    const { ModalController } = await import("@theme/modal-controller");
    if (!window.__quickfireModalController) {
      window.__quickfireModalController = new ModalController();
    }
    return window.__quickfireModalController;
  } catch (err) {
    console.error("Failed to load ModalController:", err);
    return null;
  }
}

/**
 * Opens the Klaviyo Back In Stock popup modal and populates it with product & variant info.
 * @param {HTMLElement|Object} triggerOrOptions
 */
export async function openKlaviyoBISModal(triggerOrOptions) {
  if (typeof document === "undefined") return;

  const modal = document.querySelector('[data-modal="klaviyo-bis-modal"]');
  if (!modal) {
    console.warn("Klaviyo Back In Stock modal element not found in DOM.");
    return;
  }

  let variantId = "";
  let productId = "";
  let productTitle = "";
  let variantTitle = "";

  if (triggerOrOptions instanceof HTMLElement) {
    const trigger = triggerOrOptions;
    const form = trigger.closest("form");
    const mainProduct = trigger.closest("main-product");

    variantId =
      trigger.dataset.variantId ||
      form?.querySelector('input[name="id"]')?.value ||
      mainProduct?.dataset.variantId ||
      "";

    productId =
      trigger.dataset.productId ||
      mainProduct?.dataset.productId ||
      "";

    productTitle =
      trigger.dataset.productTitle ||
      mainProduct?.querySelector(".product-title")?.textContent?.trim() ||
      document.querySelector("h1.product-title, h2.product-title")?.textContent?.trim() ||
      "";

    variantTitle =
      trigger.dataset.variantTitle ||
      mainProduct?.querySelector(".variant-size-options input:checked + label")?.textContent?.trim() ||
      "";
  } else if (triggerOrOptions && typeof triggerOrOptions === "object") {
    variantId = triggerOrOptions.variantId || "";
    productId = triggerOrOptions.productId || "";
    productTitle = triggerOrOptions.productTitle || "";
    variantTitle = triggerOrOptions.variantTitle || "";
  }

  // Populate modal inputs and labels
  const variantInput = modal.querySelector("[data-bis-variant-id]");
  if (variantInput) {
    variantInput.value = variantId;
  }

  const productInput = modal.querySelector("[data-bis-product-id]");
  if (productInput) {
    productInput.value = productId;
  }

  const titleEl = modal.querySelector("[data-bis-product-title]");
  if (titleEl && productTitle) {
    titleEl.textContent = productTitle;
  }

  const variantTitleEl = modal.querySelector("[data-bis-variant-title]");
  if (variantTitleEl) {
    if (variantTitle && variantTitle.toLowerCase() !== "default title") {
      variantTitleEl.textContent = `Selected: ${variantTitle}`;
      variantTitleEl.classList.remove("hidden");
    } else {
      variantTitleEl.textContent = "";
      variantTitleEl.classList.add("hidden");
    }
  }

  // Reset modal state
  const formContainer = modal.querySelector("[data-bis-form-container]");
  const successContainer = modal.querySelector("[data-bis-success-container]");
  const errorEl = modal.querySelector("[data-bis-error]");
  const submitBtn = modal.querySelector("[data-bis-submit]");

  if (formContainer) formContainer.classList.remove("hidden");
  if (successContainer) successContainer.classList.add("hidden");
  if (errorEl) errorEl.classList.add("hidden");

  if (submitBtn) {
    submitBtn.disabled = false;
    submitBtn.textContent = submitBtn.dataset.originalText || "Notify me";
  }

  // Present modal
  const mc = await getModalController();
  if (mc) {
    mc.presentModal("klaviyo-bis-modal");
  } else {
    modal.classList.add("modal-overlay--visible");
    modal.removeAttribute("hidden");
    modal.removeAttribute("inert");
  }

  // Focus email input
  window.setTimeout(() => {
    modal.querySelector('input[type="email"]')?.focus();
  }, 100);
}

/**
 * Handles submission of the Klaviyo Back In Stock modal form.
 */
async function handleModalSubmit(event) {
  event.preventDefault();

  const form = event.currentTarget;
  const modal = form.closest('[data-modal="klaviyo-bis-modal"]');
  const emailInput = form.querySelector('input[type="email"]');
  const variantInput = form.querySelector("[data-bis-variant-id]");
  const submitBtn = form.querySelector("[data-bis-submit]");
  const errorEl = modal?.querySelector("[data-bis-error]");
  const formContainer = modal?.querySelector("[data-bis-form-container]");
  const successContainer = modal?.querySelector("[data-bis-success-container]");
  const successMsg = modal?.querySelector("[data-bis-success-message]");

  const email = emailInput?.value?.trim() || "";
  const variantId = variantInput?.value?.trim() || "";
  const apiKey = form.dataset.klaviyoApiKey || KLAVIYO_DEFAULT_API_KEY;

  if (!email || !/@/.test(email)) {
    if (errorEl) {
      errorEl.textContent = "Please enter a valid email address.";
      errorEl.classList.remove("hidden");
    }
    emailInput?.focus();
    return;
  }

  if (!variantId) {
    if (errorEl) {
      errorEl.textContent = "Please select a size or variant before subscribing.";
      errorEl.classList.remove("hidden");
    }
    return;
  }

  if (errorEl) errorEl.classList.add("hidden");

  if (submitBtn) {
    if (!submitBtn.dataset.originalText) {
      submitBtn.dataset.originalText = submitBtn.textContent.trim();
    }
    submitBtn.disabled = true;
    submitBtn.textContent = "Sending...";
  }

  const payload = {
    data: {
      type: "back-in-stock-subscription",
      attributes: {
        channels: ["EMAIL"],
        profile: {
          data: {
            type: "profile",
            attributes: { email },
          },
        },
      },
      relationships: {
        variant: {
          data: {
            type: "catalog-variant",
            id: `$shopify:::$default:::${variantId}`,
          },
        },
      },
    },
  };

  try {
    const response = await executeKlaviyoSubscription(apiKey, payload);

    if (response.status === 202 || response.ok) {
      if (formContainer) formContainer.classList.add("hidden");
      if (successContainer) successContainer.classList.remove("hidden");
      if (successMsg) {
        successMsg.textContent = `Thanks! We'll email ${email} as soon as this item is back in stock.`;
      }
    } else {
      const data = await response.json().catch(() => ({}));
      const msg =
        data?.errors?.[0]?.detail ||
        data?.error_message ||
        "Unable to process your request. Please try again later.";
      if (errorEl) {
        errorEl.textContent = msg;
        errorEl.classList.remove("hidden");
      }
    }
  } catch (err) {
    console.error("Klaviyo Back In Stock subscription error:", err);
    if (errorEl) {
      errorEl.textContent = "Unable to process your request. Please check your connection and try again.";
      errorEl.classList.remove("hidden");
    }
  } finally {
    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.textContent = submitBtn.dataset.originalText || "Notify me";
    }
  }
}

/**
 * Initializes listeners for the Klaviyo Back In Stock modal form and trigger buttons.
 */
function initModalListeners() {
  if (typeof document === "undefined") return;

  const modalForm = document.querySelector(".klaviyo-bis-form");
  if (modalForm && !modalForm.__klaviyoBound) {
    modalForm.__klaviyoBound = true;
    modalForm.addEventListener("submit", handleModalSubmit);
  }
}

// Delegated click listener on document for any .klaviyo-bis-trigger
if (typeof document !== "undefined") {
  document.addEventListener("click", (event) => {
    const trigger = event.target.closest?.(".klaviyo-bis-trigger, [klaviyo-bis-trigger]");
    if (!trigger) return;

    event.preventDefault();
    openKlaviyoBISModal(trigger);
  });

  // Bind submit handler on DOM ready or immediate
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initModalListeners);
  } else {
    initModalListeners();
  }
}

/**
 * Ensures Klaviyo back-in-stock buttons on products with attributes.attrs_919 say "NOTIFY ME"
 */
export function applyAttrs919BisButtonText() {
  if (typeof document === "undefined") return;
  const bisButtons = document.querySelectorAll(
    "#klaviyo-bis-button-container > button, .klaviyo-bis-button-container > button"
  );
  bisButtons.forEach((btn) => {
    const parentMain = btn.closest("main-product, [data-attrs-919='true'], .is-attrs-919");
    if (parentMain && (parentMain.getAttribute("data-attrs-919") === "true" || parentMain.classList.contains("is-attrs-919"))) {
      const isAvailable = parentMain.getAttribute("data-available") === "true" && parentMain.getAttribute("data-variant-available") !== "false";
      if (!isAvailable) {
        if (btn.textContent?.trim() !== "NOTIFY ME") {
          btn.textContent = "NOTIFY ME";
        }
        btn.setAttribute("aria-label", "NOTIFY ME");
      }
    }
  });
}

if (typeof document !== "undefined") {
  const bisObserver = new MutationObserver(() => {
    applyAttrs919BisButtonText();
  });
  const startObserving = () => {
    if (document.body) {
      bisObserver.observe(document.body, { childList: true, subtree: true });
      applyAttrs919BisButtonText();
    }
  };
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", startObserving);
  } else {
    startObserving();
  }
}

// Setup KlaviyoOnsite compatibility shim
if (typeof window !== "undefined") {
  window.KlaviyoOnsite = window.KlaviyoOnsite || {};
  window.KlaviyoOnsite.BackInStock = window.KlaviyoOnsite.BackInStock || {
    initialize: () => {
      initModalListeners();
      applyAttrs919BisButtonText();
      setTimeout(applyAttrs919BisButtonText, 100);
      setTimeout(applyAttrs919BisButtonText, 300);
    },
  };
  window.openKlaviyoBISModal = openKlaviyoBISModal;
}

/**
 * Legacy class for inline PDP notify forms (snippets/klaviyo-bis-form.liquid).
 */
export class KlaviyoBackInStock {
  constructor(formSelector = "form.notify-form") {
    this.form = document.querySelector(formSelector);
    if (!this.form) return;

    this.messageContainer = document.querySelector(".pdp-sold-out-form__form .form-message");
    this.apiKey = this.form.dataset.klaviyoApiKey || KLAVIYO_DEFAULT_API_KEY;
    this.productId = this.form.dataset.productId;

    if (this.messageContainer && this.apiKey && this.productId) {
      this.bindEvents();
    }
  }

  bindEvents() {
    this.form.addEventListener("submit", (e) => this.handleSubmit(e));
  }

  async handleSubmit(event) {
    event.preventDefault();

    const emailInput = this.form.querySelector('input[type="email"]');
    const notifyButton = this.form.querySelector("button");

    this.setLoadingState(notifyButton, true);

    try {
      const response = await executeKlaviyoSubscription(
        this.apiKey,
        this.buildPayload(emailInput.value),
      );

      if (response.status === 202 || response.ok) {
        this.showMessage("Success! You will be notified when the product is back in stock.");
        emailInput.value = "";
      } else {
        const data = await response.json().catch(() => ({}));
        this.showMessage("Error: " + (data.error_message || "Unable to process your request."));
      }
    } catch (error) {
      console.error("Klaviyo BIS error:", error);
      this.showMessage("Error: Unable to process your request. Please try again later.");
    } finally {
      this.setLoadingState(notifyButton, false);
    }
  }

  buildPayload(email) {
    return {
      data: {
        type: "back-in-stock-subscription",
        attributes: {
          channels: ["EMAIL"],
          profile: {
            data: {
              type: "profile",
              attributes: { email },
            },
          },
        },
        relationships: {
          variant: {
            data: {
              type: "catalog-variant",
              id: `$shopify:::$default:::${this.productId}`,
            },
          },
        },
      },
    };
  }

  setLoadingState(button, isLoading) {
    button.textContent = isLoading ? "Sending..." : "Notify Me";
    button.disabled = isLoading;
  }

  showMessage(message) {
    if (this.messageContainer) {
      this.messageContainer.textContent = message;
    }
  }
}

export const klaviyoBackInStock = new KlaviyoBackInStock();
